import { Button, Col, Row, Select } from 'antd';
import {
    difference,
    filter,
    forEach,
    get,
    includes,
    isEmpty,
    isEqual,
    isUndefined,
    map,
    remove,
    some,
    toLower,
} from 'lodash';
import QueueAnim from 'rc-queue-anim';
import React, {
    lazy,
    RefObject,
    Suspense,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withAccountingSystemHandler } from '../../components/common/AccountingSystemHandler';
import ActionBar from '../../components/common/ActionBar';
import { customFieldIndicator } from '../../components/common/FilterBar';
import FontAwesome from '../../components/common/FontAwesome';
import { withPageViewHandler } from '../../components/common/PageViewHandler';
import VirtualizedList from '../../components/common/VirtualizedList';
import CustomerItemComponent from '../../components/sales/CustomerItemComponent';
import {
    CUSTOMERS_PAGE,
    PAGE_NAMES_FOR_VIEW,
} from '../../config/tableAndPageConstants';
import {
    customersATBCalculationOptions,
    customersSortByOptions,
    getCustomersFilterBarFilters,
} from '../../constants/customersSortAndFilters';
import { ApplicationState } from '../../store';
import {
    CompaniesState,
    Company,
    CompanyCustomFieldConfigure,
} from '../../store/companies/types';
import {
    getCustomersForOrganisationRequestAction,
    getOrganisationCustomerDataRequestAction,
    setCustomerSelectedIdRequestAction,
    updateCustomersFiltersAction,
    updateCustomersSortByAndStateAction,
    updateCustomersTableFilterAction,
} from '../../store/customers/actions';
import { initialState } from '../../store/customers/reducer';
import {
    getCustomerSelectedId,
    getCustomerUILabel,
} from '../../store/customers/sagas';
import {
    CustomerOrganisationCurrency,
    CustomersState,
    GetCustomersRequestPayload,
} from '../../store/customers/types';
import { getCurrentUser } from '../../store/users/sagas';
import {
    computeTableScroll,
    downloadToExcelModalResponseHandler,
    emptyPredefinedFilterOnAppliedFilters,
    getSortFieldsWithCustomFields,
    getUsedSortByOptionsIfCloud,
    insertAt,
    populateDownloadToExcelModalDisplayMessage,
    populateDownloadToExcelModalTitle,
    removeAppliedFiltersForApiRequest,
    replaceInstancesOfCustomerString,
    useCompanyFlagValue,
} from '../../utils/commonFunctions';
import {
    DynamicObject, ResponseModalObject
} from '../../utils/commonInterfaces';
import { Currency, Organisation } from '../../store/organisations/types';
import { OrganisationIdAttribute } from '../../constants/authUserAttributes';
import {
    IsOrganisationViewAttribute
} from '../../constants/authUserAttributes';
import { customersOrganisationSummaryPageQuery, customersOrganisationSummaryQueryName, defaultPageSizeForReport } from '../../constants/downloadToExcel';
import { downloadToExcelAction } from '../../store/common/actions';
import SpecialisedReportsDrawer from '../common/SpecialisedReportsDrawer';
import { SpecialisedReports } from '../../constants/common';
import { AtbViewType } from '../../constants/settings';

const CustomerItemDetailsDrawerComponent = lazy(
    () => import('../../components/sales/CustomerItemDetailsDrawerComponent')
);
const ModalWithSpinner = lazy(
    () => import('../../components/common/ModalWithSpinner')
);
const FilterBar = lazy(() => import('../../components/common/FilterBar'));

const { Option } = Select;

let lastSelectedOrganisationId: string | null = null;
let resetTableScroll = false;
let isRefetching = false;
let fetchCustomersRequired = false;
let skipListenToPreconfiguredFilter = false;
const tablePageSize = CUSTOMERS_PAGE.pageSize;
const pageName = PAGE_NAMES_FOR_VIEW.CUSTOMERS_PAGE;
let initialTableFilter: undefined | string = undefined;
interface IProps {
    readonly region: string;
    readonly functionRefObj: any;
    readonly customFieldsFilterList: [];
    readonly isUsingCloudImportType: boolean; // [TODO]
    readonly handlePageViewSelection: (
        tableFilterValue: string | undefined,
        applyFiltersFunction: Function,
        actionBarRefCurrent?: any,
        pageName?: string
    ) => void;
    readonly conversationRate?: number,
    readonly currencies?: Currency[],
    readonly usingCustomerWorkflow: boolean,
    readonly companies: Company[];
}
const OrganisationCustomersContent: React.FC<IProps> = ({
    region,
    functionRefObj,
    customFieldsFilterList,
    isUsingCloudImportType,
    handlePageViewSelection,
    conversationRate,
    currencies,
    usingCustomerWorkflow,
    companies
}: IProps) => {
    const dispatch = useDispatch();
    const customerLabel = useSelector(getCustomerUILabel);
    const selectedId = useSelector(getCustomerSelectedId);
    const currentUser = useSelector(getCurrentUser);
    const [filterItemList, setFilterItemList] = useState<DynamicObject[]>([]);

    const isOrgView = get(currentUser, IsOrganisationViewAttribute) === '1';

    const isDueDateView: boolean = isOrgView;

    const organisationLoading: Organisation = useSelector(
        (state: ApplicationState) => state.organisations.loading
    );

    const selectedUserOrganisation: Organisation = useSelector(
        (state: ApplicationState) =>
            state.organisations.selectedUserOrganisation
    );

    const userCompanies: CompaniesState['userCompanies'] = useSelector(
        (state: ApplicationState) => state.companies.userCompanies
    );
    const orgUserCompanies = filter(userCompanies, uc => companies.map(c => c.CompanyId).includes(uc.Company.CompanyId));
    const usingMultipleWorkflow: boolean = some(orgUserCompanies, uc => uc.Company.UsingMultipleWorkflow);
    const isCalendarView = useCompanyFlagValue(AtbViewType.CalendarView);
    
    const actionBarRef: RefObject<DynamicObject | null | undefined> = useRef();

    const [customerDetailsDrawer, setCustomerDetailsDrawer] = useState<{
        visible: boolean;
        activeTab: undefined | string;
    }>({
        visible: false,
        activeTab: undefined,
    });

    const customersState: CustomersState = useSelector(
        (state: ApplicationState) => state.customers
    );

    const [customersTableFilter, setCustomersTableFilter] = useState<
        string | undefined
    >(
        isEqual(customersState.filters, initialState.filters)
            ? initialState.tableFilter
            : customersState.tableFilter
    );

    const [tableFilters, setTableFilters] = useState<any>(
        customersState.filters
    );

    const [tableRowSelection, setTableRowSelection] = useState<{
        selectedRowKeys: string[];
        unselectedRowKeys: string[];
    }>({
        selectedRowKeys: [],
        unselectedRowKeys: [],
    });

    const [actionBarItemsState, setActionBarItemsState] = useState<{
        editButtonLoading: boolean;
    }>({
        editButtonLoading: false,
    });

    /**
     * Function to get used sort options
     */
    const getCustomersSortByOptions = () => {
        const usedSortOptions = customersSortByOptions(tableSortState.atbType === customersATBCalculationOptions[1].value, isCalendarView)
        return getUsedSortByOptionsIfCloud(
            usedSortOptions,
            isUsingCloudImportType
        );
    };

    /**
     * Function for checking if sort value is in options and set the first one if not.
     */
    const checkSortBySelectedIfAvailable = () => {
        if (!selectedUserOrganisation) return;
        const usedSortByOptions = getCustomersSortByOptions();
        const usedSortValues = map(
            usedSortByOptions,
            (sortOpt) => sortOpt.value
        );
        forEach(
            customFieldsFilterList,
            ({ Type, FieldName }: CompanyCustomFieldConfigure) => {
                usedSortValues.push(
                    `${customFieldIndicator}${Type}--${FieldName}`
                );
            }
        );
        if (!includes(usedSortValues, customersState.sortBy)) {
            setTimeout(() => {
                const newSortValues = {
                    ...tableSortState,
                    sortBy: initialState.sortBy,
                };

                updateTableSortStateObject(newSortValues);
                dispatch(updateCustomersSortByAndStateAction(newSortValues));
            }, 200);
        }
    };

    useEffect(checkSortBySelectedIfAvailable, [
        selectedUserOrganisation,
        customersState.sortBy,
        isCalendarView
    ]);

    /**
     * Function that checks if there are filters from redux for this page.
     * Returns boolean.
     */
    const getHasFiltersOnRedux = () => {
        let hasFiltersOnRedux = false;
        forEach(customersState.filters, (filterValue: any) => {
            if (!isEmpty(filterValue)) {
                hasFiltersOnRedux = true;
                return false; // terminates the foreach
            }
        });

        return hasFiltersOnRedux;
    };

    const [showConditions, setShowConditions] = useState<{
        filterBar: boolean;
        filterEllipsis: boolean;
        allSelected: boolean;
        downloadToExcel: boolean;
        specialisedReportsPanel: boolean;
    }>({
        filterBar: getHasFiltersOnRedux(),
        filterEllipsis: false,
        allSelected: false,
        downloadToExcel: false,
        specialisedReportsPanel: false
    });

    const [tableSortState, setTableSortState] = useState<{
        sortBy: string;
        atbType: number;
        sortAscending: boolean;
    }>({
        sortBy: customersState.sortBy,
        atbType: isDueDateView
            ? customersATBCalculationOptions[1].value
            : customersATBCalculationOptions[0].value,
        sortAscending: customersState.sortAscending,
    });

    const [tableCurrentPage, setTableCurrentPage] = useState<number>(
        get(customersState, 'pageData.currentPage', 0)
    );

    /**
     * Function that prepares the payload for the fetch request (either in table or excel report).
     * @param currentPage
     * @param pageSize
     */
    const generatePayloadForRequest = (
        currentPage: number,
        pageSize: number
    ) => {
        let allFilters = {
            ...customersState.filters
        };

        if(companies && isEmpty(allFilters.CompanyIds)) {
            allFilters.CompanyIds = companies.map(c => c.CompanyId);
        }

        const payload: GetCustomersRequestPayload = {
            filters: allFilters,
            sortBy: customersState.sortBy,
            sortAscending: customersState.sortAscending,
            pageSize,
            tableFilter: customersTableFilter,
            atbType: customersState.atbType,
            currentPage: currentPage,
            isUsingCloudImportType,
            usingMultipleWorkflow,
            usingCustomerWorkflow,
            region: region,
            conversionCurrency: conversationRate,
            organisationCurrencies: currencies ? currencies.map(item => ({
                Value: item.Value,
                Rate: item.Rate
            })) : []
        };

        return payload;
    };

    /**
     * Function that handles fetching of customers (calls an action that triggers an API call).
     * @param currentPage - page dependent on scroll
     * @param pageSize - number of items in page
     */
    const fetchCustomers = (
        currentPage = tableCurrentPage,
        pageSize = tablePageSize
    ) => {
        if (isEmpty(selectedUserOrganisation)) return;
        const payload = generatePayloadForRequest(currentPage, pageSize);

        if (!isRefetching) resetTableScroll = false;
        dispatch(getCustomersForOrganisationRequestAction(payload));
    };

    /**
     * Function called when more data from list is requested
     * by scrolling down.
     */
    const handleFetch = () => {
        if (
            isUndefined(initialTableFilter) ||
            isEmpty(selectedUserOrganisation) ||
            customersState.loading
        )
            return;

        if (!customersState.pageData.hasNextPage) return;

        const nextPage = tableCurrentPage + 1;
        setTableCurrentPage(nextPage);
        fetchCustomers(nextPage);
    };

    const setInitialFilterATBType = () => {
        dispatch(
            updateCustomersSortByAndStateAction({
                ...tableSortState,
                atbType:
                    isDueDateView === true
                        ? customersATBCalculationOptions[1].value
                        : customersATBCalculationOptions[0].value,
            })
        );
    };

    useEffect(setInitialFilterATBType, [isDueDateView]);

    /**
     * Function called when page loads, changes in filters, and sort options,
     * or company is changed.
     */
    const callFetchCustomers = () => {
        if (!selectedUserOrganisation) return;
        const organisationIdCognito = get(currentUser, OrganisationIdAttribute);
        const selectedOrganisationId = get(selectedUserOrganisation, 'OrganisationId');

        if (organisationIdCognito === selectedOrganisationId) {
            if (lastSelectedOrganisationId !== selectedOrganisationId) {
                lastSelectedOrganisationId = selectedOrganisationId;
                resetAllSelectedRowKeys();
                checkAllTableFiltersOnCompanySwitch();
                resetTableScrollAndPageData();
            }
            if (!organisationLoading) fetchCustomers(0);
        }
    };

    /**
     * Function for comparing component and redux state then setting the correct values.
     */
    const checkAllTableFiltersOnCompanySwitch = () => {
        const { filters, sortBy, sortAscending } = customersState;
        if (!isEqual(filters, tableFilters)) {
            setTableFilters(filters);
        }

        if (
            sortBy !== tableSortState.sortBy ||
            sortAscending !== tableSortState.sortAscending
        ) {
            updateTableSortStateObject({
                sortBy,
                sortAscending,
            });
        }
    };

    useEffect(callFetchCustomers, [
        customersState.sortBy,
        customersState.sortAscending,
        customersState.filters,
        customersState.atbType,
        organisationLoading,
        selectedUserOrganisation,
    ]);

    // on Unmount
    useEffect(() => {
        return () => {
            lastSelectedOrganisationId = null;
            skipListenToPreconfiguredFilter = true;
        };
    }, []);

    const selectedKeysName = 'Id';

    /**
     * Function that checks if the row is selected.
     * Specifically if `Select all` is clicked and the customer data is updated,
     * it marks all the checkbox depending on the selected value.
     */
    const checkRowSelectionState = () => {
        if (showConditions.allSelected) {
            const selectedKeys = difference(
                map(customersState.data, selectedKeysName),
                tableRowSelection.unselectedRowKeys
            );

            updateTableRowSelection({
                selectedRowKeys: selectedKeys,
            });
        }
    };

    useEffect(checkRowSelectionState, [customersState.data]);

    /**
     * Action Bar Functions
     */
    /**
     * Function that updates the redux table filters and state table filters.
     * @param filter - dropdown view value
     * @param refetch - boolean indicator if fetching of items is to be called
     */
    const changeCustomersTableFilter = (
        filter: string,
        refetch: boolean = true
    ) => {
        if (!initialTableFilter) {
            initialTableFilter = filter;
        } else {
            if (filter !== initialTableFilter) {
                updateShowConditionsObject({
                    allSelected: false,
                    filterBar: true,
                });
            }
        }

        if (filter !== customersState.tableFilter) {
            skipListenToPreconfiguredFilter = false;
        }

        setCustomersTableFilter(filter);
        resetTableScrollAndPageData();
        if (refetch && filter === customersState.tableFilter) {
            handleCustomerFilterRefresh();
        } else {
            dispatch(updateCustomersTableFilterAction(filter));
        }
    };

    /**
     * Function responsible for refetching tasks data after an update or when clicking the refresh button.
     */
    const refetchListAndResetScroll = () => {
        isRefetching = true;
        resetTableScrollAndPageData();
        fetchCustomers(0);
    };

    /**
     * Function called when refresh button is clicked (upper left near table filter dropdown).
     */
    const handleCustomerFilterRefresh = () => {
        fetchCustomersRequired = true;
        skipListenToPreconfiguredFilter = false;
        resetAllSelectedRowKeys();
        refetchListAndResetScroll();
    };

    /**
     * Listener function called when the table filter value for the dropdown at the upper left has been changed.
     */
    const listenToPreConfiguredFilter = () => {
        if (skipListenToPreconfiguredFilter) return;
        const { tableFilter } = customersState;
        if (tableFilter === initialTableFilter) {
            closeFilterBar();

            if (fetchCustomersRequired) {
                fetchCustomers(0);
                fetchCustomersRequired = false;
            }
        } else {
            handlePageViewSelection(
                tableFilter,
                applyFilters,
                actionBarRef.current,
                pageName
            );
        }
    };

    useEffect(listenToPreConfiguredFilter, [customersState.tableFilter]);

    /**
     * Function that populates the customer table filter (upper left).
     * @param menu
     */
    const populateCustomerFilterSelectDropdownRender = (
        menu: React.Component
    ) => (
        <div>
            {menu}
            {/* <Divider className="action-bar-divider" />
            <div
                className="pa-12 cursor-p"
                onMouseDown={() => console.log('Add Custom View Clicked')}
            >
                Add custom view
            </div> */}
        </div>
    );

    /**
     * Function that populates the label for button (Select all or Deselect all).
     */
    const populateSelectDeselectAllLabel = () => {
        const allDataLoaded = customersState.pageData.hasNextPage === false;

        let selectDeselectLabel = '';
        if (
            !isEmpty(customersState.data) &&
            ((tableRowSelection.unselectedRowKeys.length === 0 &&
                showConditions.allSelected) ||
                (allDataLoaded &&
                    customersState.data.length ===
                    tableRowSelection.selectedRowKeys.length))
        ) {
            selectDeselectLabel = 'Deselect';
        } else {
            selectDeselectLabel = 'Select';
        }

        return `${selectDeselectLabel} all`;
    };

    /**
     * Function called when `Select all` button is clicked.
     */
    const selectAllRows = () => {
        const selectAllCondition =
            isEmpty(tableRowSelection.selectedRowKeys) ||
            tableRowSelection.selectedRowKeys.length <
            customersState.data.length;

        updateShowConditionsObject({
            allSelected: selectAllCondition,
        });

        if (selectAllCondition) {
            const idKeys: string[] = map(customersState.data, selectedKeysName);

            updateTableRowSelection({
                selectedRowKeys: idKeys,
                unselectedRowKeys: [],
            });
        } else {
            updateTableRowSelection({
                selectedRowKeys: [],
            });
        }
    };

    /**
     * Function called when sort order button is clicked (ascending or descending).
     * @param sortAscending - boolean - true for ascending and false for descending
     */
    const changeSortOrder = async (sortAscending: boolean) => {
        await resetTableScrollAndPageData();
        dispatch(
            updateCustomersSortByAndStateAction({
                ...tableSortState,
                sortAscending,
            })
        );
    };

    const downloadDisabled =
        customersState.loading || isEmpty(customersState.data);

    /**
     * Function called when Download to Excel button is clicked.
     */
    const downloadToExcelHandler = (specializedReportType?: string) => {
        updateShowConditionsObject({
            downloadToExcel: true,
            filterEllipsis: false,
        });

        let organisationCurrencies: CustomerOrganisationCurrency[] = currencies ? currencies.map(item => ({
            Value: item.Value,
            Rate: item.Rate
        })) : [];

        const filterAndPageProps = generatePayloadForRequest(
            0,
            defaultPageSizeForReport
        );

        const {
            filters,
            atbType,
            sortBy,
            sortAscending,
            usingMultipleWorkflow,
            usingCustomerWorkflow,
        } = filterAndPageProps;

        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            'customer'
        );
        const sortObject = getSortFieldsWithCustomFields(sortBy);

        const variables = {
            ...cleanFilters,
            ATBType: atbType,
            // SortField: sortBy,
            ...sortObject,
            Ascending: sortAscending,
            IsCloudImportType: isUsingCloudImportType,
            IsMultipleWorkflow: usingMultipleWorkflow,
            UsingCustomerWorkflow: usingCustomerWorkflow,
            Region: region,
            ConversionCurrency: conversationRate,
            OrganisationCurrencies: organisationCurrencies
        };

        const payload = {
            Query: customersOrganisationSummaryPageQuery,
            OperationName: customersOrganisationSummaryQueryName,
            Variables: JSON.stringify(variables),
            PageName: PAGE_NAMES_FOR_VIEW.CUSTOMERS_ORGANISATION_PAGE,
            SpecializedReport: specializedReportType
        };

        dispatch(downloadToExcelAction(payload, downloadToExcelModalResponse));
    };

    /**
     * Callback function after calling the download to excel api.
     * @param param0 - response with type `ResponseModalObject`
     */
    const downloadToExcelModalResponse = ({
        IsSuccess,
        Messages,
    }: ResponseModalObject) => {
        updateShowConditionsObject({
            downloadToExcel: false,
            filterEllipsis: false,
        });
        downloadToExcelModalResponseHandler(IsSuccess, Messages);
    };

    /**
     * Function for populating the upper section for the ellipsis popover.
     */
    const populateFilterEllipsisPopoverTitle = () => (
        <div className="pop-action-title">
            <Button
                type="link"
                disabled={downloadDisabled}
                onClick={() => {
                    setShowConditions((prevState: { filterBar: boolean }) => ({
                        ...showConditions,
                        filterEllipsis: false,
                        filterBar: !prevState.filterBar,
                    }));
                }}
            >
                <FontAwesome icon={['fas', 'filter']} className="mr-10" />
                Filter
            </Button>
            <br />
            <Button
                type="link"
                disabled={downloadDisabled}
                onClick={() => {
                    setTableSortState((prevState: any) => {
                        const sortOrder = !prevState.sortAscending;
                        changeSortOrder(sortOrder);
                        return {
                            ...prevState,
                            sortAscending: sortOrder,
                        };
                    });
                }}
            >
                <FontAwesome
                    icon={[
                        'fas',
                        `sort-amount-${customersState.sortAscending ? 'down' : 'up-alt'
                        }`,
                    ]}
                    className="mr-10"
                />
                Change sort order
            </Button>
            <br />
            <Button
                type="link"
                onClick={() => downloadToExcelHandler()}
                disabled={downloadDisabled}
            >
                <FontAwesome
                    icon={['fas', 'cloud-download-alt']}
                    className="mr-10"
                />
                Download to Excel
            </Button>
            <br />
            <Button
                type="link"
                onClick={() => {
                    updateShowConditionsObject({
                        filterEllipsis: false,
                        specialisedReportsPanel: true
                    })
                }}
                disabled={downloadDisabled}
            >
                <FontAwesome
                    icon={['fas', 'cloud-download-alt']}
                    className="mr-10"
                />
                Specialised Reports
            </Button>
        </div>
    );

    /**
     * Function that populates the lower section of the ellipsis popover.
     */
    const populateFilterEllipsisPopoverContent = () => {
        const usedSortOptions = getCustomersSortByOptions();

        return (
            <div className="pop-action-content">
                <div className="mb-10">
                    <span>Sort by</span>
                </div>
                <div>
                    <Select
                        onChange={(sortBySelected: string) =>
                            updateTableSortStateObject({
                                sortBy: sortBySelected,
                            })
                        }
                        value={tableSortState.sortBy}
                    >
                        {map(
                            usedSortOptions,
                            ({
                                label,
                                value,
                            }: {
                                label: any;
                                value: string;
                            }) => {
                                const labelUsed =
                                    replaceInstancesOfCustomerString(
                                        label,
                                        customerLabel
                                    );

                                return (
                                    <Option key={value} value={value}>
                                        {labelUsed}
                                    </Option>
                                );
                            }
                        )}
                        {map(
                            customFieldsFilterList,
                            ({
                                Type,
                                Number: CFNumber,
                                FieldName,
                            }: CompanyCustomFieldConfigure) => (
                                <Option
                                    key={Type + CFNumber + FieldName}
                                    value={`${customFieldIndicator}${Type}--${FieldName}`}
                                >
                                    {FieldName}
                                </Option>
                            )
                        )}
                    </Select>
                </div>
                <br />
                <div className="ta-right">
                    <Button
                        type="primary"
                        disabled={
                            customersState.loading ||
                            (tableSortState.sortBy === customersState.sortBy &&
                                tableSortState.atbType ===
                                customersState.atbType)
                        }
                        onClick={applySortedByAndState}
                    >
                        Apply
                    </Button>
                </div>
            </div>
        );
    };

    /**
     * Filter Bar Functions
     */
    /**
     * Function that updates the table filters state, also the table filters redux state.
     * Called when `Apply filters` button is clicked.
     * @param filters
     * @param fromFilterBar - boolean indicator if called from Apply filters button in FilterBar component
     * @param fromQuickSearch - boolean indicator if called from the quick search component
     */
    const applyFilters = async (
        filters?: GetCustomersRequestPayload['filters'],
        fromFilterBar?: boolean,
        fromQuickSearch?: boolean
    ) => {
        
        if (!filters) {
            setTableFilterToInitialState();
        } else {
            if (fromFilterBar) {
                emptyPredefinedFilterOnAppliedFilters(
                    filters,
                    tableFilters,
                    customersTableFilter,
                    fromFilterBar,
                    () => {
                        changeCustomersTableFilter('');
                    }
                );
            }
        }
        
        let appliedFilters: any = filters || initialState.filters;
        const savedSortState = get(appliedFilters, 'tableSortState');
        if (savedSortState) {
            setTableSortState(savedSortState);
            applySortedByAndState(savedSortState);
            delete appliedFilters.tableSortState;
        }

        await resetTableScrollAndPageData();
        await setTableFilters(appliedFilters);
        await dispatch(updateCustomersFiltersAction(appliedFilters));
        await resetAllSelectedRowKeys(fromQuickSearch);
    };
    functionRefObj.applyFilters = applyFilters;

    /**
     * Function that sets the value of the dropdown filter next to refresh button to it's initial state.
     */
    const setTableFilterToInitialState = () => {
        if (customersState.tableFilter !== initialTableFilter) {
            setCustomersTableFilter(initialTableFilter);

            if (!isUndefined(initialTableFilter))
                changeCustomersTableFilter(initialTableFilter);

            resetAllSelectedRowKeys();
        }
    };

    /**
     * Function called when `Apply` button is clicked inside the ellipsis popover,
     * also hides the ellipsis popover after `Save` is clicked.
     * @param savedValues - optional param - used when a specific tableSortState value is passed.
     */
    const applySortedByAndState = async (savedValues?: any) => {
        await resetTableScrollAndPageData();
        const usedValues = get(savedValues, 'sortBy')
            ? savedValues
            : tableSortState;
        await dispatch(updateCustomersSortByAndStateAction(usedValues));
        await updateShowConditionsObject({
            filterEllipsis: false,
        });
    };

    /**
     * Common function that manipulates the state object for showConditions state.
     */
    const updateShowConditionsObject = (showConditionObject: {}) => {
        setShowConditions((prevState) => ({
            ...prevState,
            ...showConditionObject
        }));
    };

    /**
     * Common function that updates the tableRowSelection state.
     * @param selectionObject - must conform to tableRowSelection object
     */
    const updateTableRowSelection = (selectionObject: {}) => {
        setTableRowSelection({
            ...tableRowSelection,
            ...selectionObject,
        });
    };

    /**
     * Common function for updating the action bar items state.
     * @param itemStateObject - must conform to actionBarItemsState object
     */
    const updateActionBarItemsState = (itemStateObject: {}) => {
        setActionBarItemsState({
            ...actionBarItemsState,
            ...itemStateObject,
        });
    };

    /**
     * Customer Details Functions
     */
    /**
     * Function called when customer details drawer is closed.
     */
    const closeCustomerDetailsDrawer = () => {
        setCustomerDetailsDrawer({
            visible: false,
            activeTab: undefined,
        });
    };

    /**
     * Table Functions
     */
    /**
     * Common function called for updating table sort state.
     * @param tableSortStateObject  - must conform to tableSortState object
     */
    const updateTableSortStateObject = (tableSortStateObject: {}) => {
        setTableSortState({
            ...tableSortState,
            ...tableSortStateObject,
        });
    };

    /**
     * Function called when checkbox is clicked (either checked/unchecked) - will call onRowSelect.
     * @param record - the data for each row (with additional `key` property to identify the row)
     */
    const onCheckboxClick = (record: DynamicObject) => {
        const selectedRowKeys = [...tableRowSelection.selectedRowKeys];
        const isCurrentlySelected = includes(selectedRowKeys, record.key);
        const newSelectedRowKeys = !isCurrentlySelected
            ? [...selectedRowKeys, record.key]
            : remove(selectedRowKeys, (key: string) => key !== record.key);

        onRowSelect(record, !isCurrentlySelected, newSelectedRowKeys, true);
    };

    /**
     * Function called when row is clicked. Responsible for showing the drawer.
     * @param record - data for each row
     * @param activeTab - the tab key to be shown upon showing the drawer
     */
    const onRowClick = (record: DynamicObject, activeTab?: string) => {
        dispatch(
            setCustomerSelectedIdRequestAction(get(record, 'Id'), () => {
                setCustomerDetailsDrawer({
                    visible: true,
                    activeTab,
                });
            })
        );
    };

    /**
     * Function triggered when after checkbox is clicked. Responsible for setting the state of selected
     * or unselected rows.
     * @param record - table row data record
     * @param selected - boolean if checked/unchecked
     * @param selectedRows - list of keys selected previously
     * @param nativeEvent
     */
    const onRowSelect = (
        record: DynamicObject,
        selected: boolean,
        selectedRows: string[],
        nativeEvent: Event | boolean
    ) => {
        // nativeEvent overridden
        const selectedRowKeys =
            nativeEvent === true ? [...selectedRows] : map(selectedRows, 'key');

        let unselectedRowKeys = [];
        if (selected) {
            unselectedRowKeys = filter(
                tableRowSelection.unselectedRowKeys,
                (unselectedKey: string) => unselectedKey !== record.key
            );
        } else {
            unselectedRowKeys = showConditions.allSelected
                ? [...tableRowSelection.unselectedRowKeys, record.key]
                : [];
        }

        updateTableRowSelection({
            selectedRowKeys,
            unselectedRowKeys,
        });
    };

    /**
     * Function triggered when action bar items with drawer/popover is clicked.
     * @param name - name of popover item
     * @param condition - optional condition whether to show the popover/drawer
     */
    const popoverOnVisibleChange = (name: string, condition?: boolean) => {
        return (visible: boolean) => {
            if (condition === undefined || condition === true) {
                updateShowConditionsObject({
                    [name]: visible,
                });
            }
        };
    };

    /**
     * Reset Functions
     */
    /**
     * Function for resetting the table scroll and sets current page to initial state.
     */
    const resetTableScrollAndPageData = async () => {
        resetTableScroll = true;
        await setTableCurrentPage(0);
    };

    /**
     * Function for resetting all selected and unselected row keys in the state.
     * Also resets some of the action bar items state
     * and the allselected indicator for `Select all` button.
     * @param excludeShowConditions - boolean indicator that checks if allSelected property in
     * `showConditions` state be excluded in resetting.
     */
    const resetAllSelectedRowKeys = (excludeShowConditions = false) => {
        // reset Selected Row Keys after change role success
        updateTableRowSelection({
            selectedRowKeys: [],
            unselectedRowKeys: [],
        });
        updateActionBarItemsState({
            editButtonDisabled: true,
        });
        if (!excludeShowConditions) {
            updateShowConditionsObject({
                allSelected: false,
            });
        }
    };

    /**
     * Function called when closing the filter bar.
     * Responsible for hiding the filter bar.
     * Also resets all the table state filters applied to initial state.
     */
    const closeFilterBar = async () => {
        await applyFilters();
        updateShowConditionsObject({
            filterBar: false,
        });
    };

    /**
     * Function called when customer batch edit is cancelled.
     */
    const onCancelBatchCustomerUpdate = () => {
        updateShowConditionsObject({
            editDrawer: false,
        });
    };

    /**
     * Function for getting the values needed when connecting to API.
     * Like the selected keys, filter object, and boolean indicator if
     * the selected keys are to be excluded from search or included.
     */
    const getSelectedCustomersValues = () => {
        let allExcept = false;
        let keysToUse = [...tableRowSelection.selectedRowKeys];

        if (showConditions.allSelected) {
            allExcept = true;
            keysToUse = [...tableRowSelection.unselectedRowKeys];
        }

        const filterObject = {
            ...tableFilters,
            ATBType: tableSortState.atbType,
        };

        return {
            allExcept,
            keysToUse,
            filterObject,
        };
    };

    /**
     * Function for populating the loading text of table.
     */
    const populateTableLoadingText = () => {
        const loadingText = `Fetching ${tableCurrentPage === 0 || isRefetching ? 'list of' : 'more'
            } ${toLower(customerLabel)}s`;

        isRefetching = false;

        return loadingText;
    };

    /**
     * Function for populating the modal title when saving the customer data.
     */
    const populateCustomerBatchSaveRequestModalTitle = () => {
        const { keysToUse } = getSelectedCustomersValues();

        return `Saving ${toLower(customerLabel)}${keysToUse.length > 1 ? 's' : ''
            } data`;
    };

    /**
     * Function for populating the modal body when saving the customer data.
     */
    const populateCustomerBatchSaveRequestModalDisplayMessage = () => {
        const { keysToUse } = getSelectedCustomersValues();

        return `Please wait while saving the ${toLower(customerLabel)}${keysToUse.length > 1 ? "s'" : "'s"
            } classification status. . .`;
    };

    /**
     * Function that checks if the Name of the page view to be saved already exists.
     * @param name - name of page view
     */
    const doesViewNameExist = (name: string) => {
        if (actionBarRef.current)
            return actionBarRef.current.doesViewNameExist(name);
    };

    /**
     * Function for getting the filter values to be used in FilterBar component.
     */
    const populateCustomerFilterBarFilters = () => {
        let customerFilterBarFilters = getCustomersFilterBarFilters(
            tableSortState.atbType,
            undefined, undefined,
            isCalendarView
        );

        const customerCompanyFilterIdx = 3;

        customerFilterBarFilters = insertAt(
            customerFilterBarFilters,
            customerCompanyFilterIdx,
            [
                {
                    filterName: 'Company',
                    filterStateName: 'CompanyIds',
                    filterElement: 'checkbox-group',
                    filterType: 'array',
                    filterOptions: !companies ? [] : companies.map(c => {
                        return {
                            label: c.Name,
                            value: c.CompanyId
                        }
                    })
                }
            ]
        );

        setFilterItemList(customerFilterBarFilters);
    };

    useEffect(populateCustomerFilterBarFilters, [
        usingCustomerWorkflow
    ]);

    /**
    * Function called when click download specialised reports is closed.
    */
    const closeSpecialisedReportsPanel = () => {
        updateShowConditionsObject({
            specialisedReportsPanel: false
        });
    };


    return (<>
        {/* Filter Bar */}
        <QueueAnim type="top" leaveReverse duration={300}>
            {showConditions.filterBar && (
                <div key="filter-bar-container">
                    <Suspense fallback={null}>
                        <FilterBar
                            pageName={pageName}
                            loading={customersState.loading}
                            applyFilters={applyFilters}
                            filters={filterItemList}
                            filterValues={customersState.filters}
                            colDivision={4}
                            closeAllPopovers={
                                customerDetailsDrawer.visible
                            }
                            closeFilterBar={closeFilterBar}
                            appliedView={customersTableFilter}
                            doesViewNameExist={doesViewNameExist}
                            customFieldsFilters={customFieldsFilterList}
                            tableSortState={{
                                sortBy: customersState.sortBy,
                                atbType: customersState.atbType,
                                sortAscending:
                                    customersState.sortAscending,
                            }}
                        />
                    </Suspense>
                </div>
            )}
            <SpecialisedReportsDrawer
                        visible={showConditions.specialisedReportsPanel}
                        onClose={closeSpecialisedReportsPanel}
                        downloadToExcelCallBack={downloadToExcelHandler}
                        reportOptions={{
                            options: SpecialisedReports,
                            loading: false
                        }}
                    />
        </QueueAnim>
        <div key="action-bar-container">
            <ActionBar
                ref={actionBarRef}
                pageName={pageName}
                loading={customersState.loading}
                filterBarOpen={showConditions.filterBar}
                actionItems={[
                    {
                        actionKey: 'customer-filter',
                        actionType: 'select-with-button',
                        selectValue: customersTableFilter,
                        selectDropdownRender:
                            populateCustomerFilterSelectDropdownRender,
                        onSelectChange: changeCustomersTableFilter,
                        buttonContent: (
                            <>
                                <FontAwesome
                                    icon={['fa', 'sync']}
                                    className="mr-8"
                                />
                                <span>Refresh</span>
                            </>
                        ),
                        buttonDisabled: customersState.loading,
                        onButtonClick: handleCustomerFilterRefresh,
                    },
                    {
                        actionKey: 'customer-select-all',
                        actionType: 'protected-button',
                        buttonDisabled: downloadDisabled,
                        onButtonClick: selectAllRows,
                        buttonContent: (
                            <>
                                <FontAwesome
                                    icon={['fas', 'check-double']}
                                />
                                <span>
                                    {populateSelectDeselectAllLabel()}
                                </span>
                            </>
                        ),
                    }
                ].filter(action => !isEmpty(action))}
                actionEllipsis={{
                    popoverVisible: showConditions.filterEllipsis,
                    popoverOnVisibleChange:
                        popoverOnVisibleChange('filterEllipsis'),
                    popoverTitle: populateFilterEllipsisPopoverTitle(),
                    popoverContent:
                        populateFilterEllipsisPopoverContent(),
                    buttonContent: (
                        <FontAwesome icon={['fas', 'ellipsis-h']} />
                    ),
                }}
            />
        </div>
        <div className="spacer-15" />
        {/* Table Section */}
        <Row key="table-container">
            <Col span={24}>
                <VirtualizedList
                    dataSource={customersState.data}
                    fetchMore={handleFetch}
                    scroll={computeTableScroll(
                        window.innerHeight - 185,
                        tablePageSize,
                        CUSTOMERS_PAGE.rowHeight
                    )}
                    resetTableScroll={resetTableScroll}
                    selectedRowKeys={tableRowSelection.selectedRowKeys}
                    rerenderTrigger={tableRowSelection.selectedRowKeys}
                    onRowClick={onRowClick}
                    onCheckboxClick={onCheckboxClick}
                    loading={customersState.loading}
                    loadingText={populateTableLoadingText()}
                    emptyText={
                        !isEmpty(customersState.errorMessages)
                            ? get(customersState, 'errorMessages.0')
                            : `No ${toLower(customerLabel)}s found`
                    }
                    hasNextPage={customersState.pageData.hasNextPage}
                    itemComponent={CustomerItemComponent}
                    itemHeight={CUSTOMERS_PAGE.rowHeight}
                    selectedId={selectedId}
                    usingCustomerWorkflow={usingCustomerWorkflow}
                    extraData={{}}
                />
            </Col>
            <Suspense fallback={null}>
                <CustomerItemDetailsDrawerComponent
                    visible={customerDetailsDrawer.visible}
                    activeTab={customerDetailsDrawer.activeTab}
                    closeDrawer={closeCustomerDetailsDrawer}
                    refetchList={refetchListAndResetScroll}
                    atbType={get(customersState, 'atbType')}
                    region={region}
                    getCustomerDataRequestAction={
                        (selectedId: string, isUsingCloudImportType: boolean) =>
                            getOrganisationCustomerDataRequestAction(selectedId, region, isUsingCloudImportType)
                    }
                />
            </Suspense>
            {customersState.saveCustomerDataLoading && (
                <Suspense fallback={null}>
                    <ModalWithSpinner
                        modalTitle={populateCustomerBatchSaveRequestModalTitle()}
                        modalVisible={
                            customersState.saveCustomerDataLoading
                        }
                        displayMessage={populateCustomerBatchSaveRequestModalDisplayMessage()}
                    />
                </Suspense>
            )}
            {showConditions.downloadToExcel && (
                <Suspense fallback={null}>
                    <ModalWithSpinner
                        modalTitle={populateDownloadToExcelModalTitle()}
                        modalVisible={showConditions.downloadToExcel}
                        displayMessage={populateDownloadToExcelModalDisplayMessage()}
                    />
                </Suspense>
            )}
        </Row>
    </>);
};

export default withPageViewHandler(
    withAccountingSystemHandler(OrganisationCustomersContent)
);
